import { useEffect, useState } from "react";
import type { StaffImportCandidateDTO } from "@easyshop/shared";
import { Button, ErrorState, Pill } from "@/components/ui";
import { api } from "@/lib/api";
import { num } from "@/lib/format";

/**
 * Reprendre l'équipe d'une boutique depuis sa caisse : les vendeurs qui y ont
 * encaissé sur 90 jours (Hiboutik — jamais l'annuaire du compte entier), ou les
 * utilisateurs de la boutique (caisse Easy Shop). Aperçu, cases à cocher, puis
 * création des fiches : rien n'est créé sans confirmation.
 */
export function ImportEquipe({ boutiqueId, onDone, onCancel }: { boutiqueId: string; onDone: (n: number) => void; onCancel: () => void }) {
  const [candidats, setCandidats] = useState<StaffImportCandidateDTO[] | null>(null);
  const [coches, setCoches] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const cle = (c: StaffImportCandidateDTO) => `${c.externalSource}:${c.externalId}`;

  useEffect(() => {
    let annule = false;
    setCandidats(null);
    setError(null);
    api
      .get<StaffImportCandidateDTO[]>("/staff/import-candidates", { boutiqueId })
      .then((r) => {
        if (annule) return;
        setCandidats(r);
        setCoches(new Set(r.filter((c) => c.actif && !c.dejaImporte).map(cle)));
      })
      .catch((e) => !annule && setError(e));
    return () => {
      annule = true;
    };
  }, [boutiqueId]);

  const importer = async () => {
    if (!candidats) return;
    setBusy(true);
    setError(null);
    try {
      const personnes = candidats.filter((c) => coches.has(cle(c))).map((c) => ({ externalSource: c.externalSource, externalId: c.externalId }));
      const r = await api.post<{ created: number }>("/staff/import", { boutiqueId, personnes });
      onDone(r.created);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const basculer = (c: StaffImportCandidateDTO) =>
    setCoches((s) => {
      const n = new Set(s);
      if (n.has(cle(c))) n.delete(cle(c));
      else n.add(cle(c));
      return n;
    });

  const source = candidats?.[0]?.externalSource;
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[12.5px] leading-[1.5] text-muted">
        {source === "EASYSHOP"
          ? "Les utilisateurs de la boutique dans la caisse Easy Shop."
          : "Les vendeurs qui ont encaissé dans cette boutique ces 90 derniers jours. Les autres comptes de la caisse ne sont pas proposés."}{" "}
        Les fiches sont créées sans heures au contrat : à compléter ensuite.
      </div>
      {error != null && <ErrorState error={error} />}
      {!candidats && !error && <div className="py-4 text-[13px] text-muted">Lecture de la caisse…</div>}
      {candidats && candidats.length === 0 && <div className="py-4 text-[13px] text-muted">Personne à reprendre pour cette boutique.</div>}
      {candidats && candidats.length > 0 && (
        <div className="flex flex-col">
          {candidats.map((c) => (
            <label key={cle(c)} className="flex items-center gap-3 border-b border-line-row py-[10px] text-[13px]">
              <input type="checkbox" checked={coches.has(cle(c))} disabled={c.dejaImporte} onChange={() => basculer(c)} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{c.name}</div>
                <div className="text-[11.5px] text-muted-2">
                  {c.email ?? "sans e-mail"}
                  {c.tickets != null ? ` · ${num(c.tickets)} ticket${c.tickets > 1 ? "s" : ""}` : ""}
                </div>
              </div>
              {c.dejaImporte ? (
                <Pill className="bg-page text-muted">Déjà dans l&apos;équipe</Pill>
              ) : !c.actif ? (
                <Pill className="bg-warn-bg text-warn-ink">Compte inactif</Pill>
              ) : null}
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Button onClick={() => void importer()} disabled={busy || coches.size === 0}>
          {busy ? "Import…" : `Ajouter ${coches.size} personne${coches.size > 1 ? "s" : ""}`}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
